import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { FileText, Info } from 'lucide-react';

export default function CaptionSettings({ selectedTeam }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [captionLabels, setCaptionLabels] = useState({
    product_code: 'Product Code',
    category: 'Category',
    color: 'Color',
    pack_sizes: 'Pack Sizes',
    fabric: 'Fabric',
    price: 'Price'
  });

  useEffect(() => {
    if (selectedTeam) {
      loadCaptionLabels();
    }
  }, [selectedTeam]);

  const loadCaptionLabels = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}`);
      if (res.data.caption_labels) {
        setCaptionLabels(res.data.caption_labels);
      }
    } catch (err) {
      toast.error('Etiketler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosInstance.put(`/teams/${selectedTeam}`, {
        caption_labels: captionLabels
      });
      toast.success('Etiketler kaydedildi! Yeni yükleme yapıldığında uygulanacak.');
      loadCaptionLabels();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setCaptionLabels({
      product_code: 'Product Code',
      category: 'Category',
      color: 'Color',
      pack_sizes: 'Pack Sizes',
      fabric: 'Fabric',
      price: 'Price'
    });
    toast.info('Varsayılan değerlere sıfırlandı. Kaydet butonuna basın.');
  };

  if (!selectedTeam) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <Card className="w-full max-w-md text-center overflow-hidden">
          <CardContent className="pt-8 pb-8 sm:pt-12 sm:pb-12">
            <FileText className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-purple-500" />
            <h2 className="text-xl sm:text-2xl font-bold mb-2">Mağaza Seçin</h2>
            <p className="text-sm sm:text-base text-gray-600">Ürün etiketlerini düzenlemek için bir mağaza seçin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          Ürün Etiketleri
        </h1>
        <p className="text-sm sm:text-base text-gray-600">Telegram mesajlarında görünecek etiketleri özelleştirin</p>
      </div>

      <div className="max-w-4xl">
        <Card className="shadow-lg border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
            <CardTitle className="text-lg sm:text-xl flex items-center">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
              Etiket Özelleştirme
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded">
              <div className="flex items-start">
                <Info className="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-700">
                  <p className="font-semibold mb-1">Bilgi:</p>
                  <p>Bu etiketler yeni ürün yüklemelerinde kullanılacaktır. Mevcut yayınlanmış ürünleri etkilemez.</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-2">
                  <Label htmlFor="product_code" className="text-sm sm:text-base">
                    📦 Ürün Kodu Etiketi
                  </Label>
                  <Input
                    id="product_code"
                    type="text"
                    value={captionLabels.product_code}
                    onChange={(e) => setCaptionLabels({ ...captionLabels, product_code: e.target.value })}
                    className="text-sm sm:text-base"
                    placeholder="Product Code"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category" className="text-sm sm:text-base">
                    🏷️ Kategori Etiketi
                  </Label>
                  <Input
                    id="category"
                    type="text"
                    value={captionLabels.category}
                    onChange={(e) => setCaptionLabels({ ...captionLabels, category: e.target.value })}
                    className="text-sm sm:text-base"
                    placeholder="Category"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="color" className="text-sm sm:text-base">
                    🎨 Renk Etiketi
                  </Label>
                  <Input
                    id="color"
                    type="text"
                    value={captionLabels.color}
                    onChange={(e) => setCaptionLabels({ ...captionLabels, color: e.target.value })}
                    className="text-sm sm:text-base"
                    placeholder="Color"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pack_sizes" className="text-sm sm:text-base">
                    📏 Beden Etiketi
                  </Label>
                  <Input
                    id="pack_sizes"
                    type="text"
                    value={captionLabels.pack_sizes}
                    onChange={(e) => setCaptionLabels({ ...captionLabels, pack_sizes: e.target.value })}
                    className="text-sm sm:text-base"
                    placeholder="Pack Sizes"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fabric" className="text-sm sm:text-base">
                    ℹ️ Detay/Kumaş Etiketi
                  </Label>
                  <Input
                    id="fabric"
                    type="text"
                    value={captionLabels.fabric}
                    onChange={(e) => setCaptionLabels({ ...captionLabels, fabric: e.target.value })}
                    className="text-sm sm:text-base"
                    placeholder="Fabric"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price" className="text-sm sm:text-base">
                    💰 Fiyat Etiketi
                  </Label>
                  <Input
                    id="price"
                    type="text"
                    value={captionLabels.price}
                    onChange={(e) => setCaptionLabels({ ...captionLabels, price: e.target.value })}
                    className="text-sm sm:text-base"
                    placeholder="Price"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  type="submit" 
                  disabled={saving}
                  className="flex-1 sm:flex-initial bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg"
                >
                  {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                </Button>
                <Button 
                  type="button"
                  onClick={resetToDefaults}
                  variant="outline"
                  className="flex-1 sm:flex-initial"
                >
                  Varsayılana Sıfırla
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0 mt-6 rounded-2xl overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
            <CardTitle className="text-lg sm:text-xl">Ürün Etiketi Önizlemesi</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="bg-gray-50 p-4 rounded-2xl font-mono text-sm">
              <p className="font-bold mb-2">DESENDER</p>
              <p>📦 {captionLabels.product_code}: DESENDER25K43106</p>
              <p>🏷️ {captionLabels.category}: CARDIGAN KNITWEAR</p>
              <p>🎨 {captionLabels.color}: BLACK</p>
              <p>📏 {captionLabels.pack_sizes}: 46-48-50-52</p>
              <p>ℹ️ {captionLabels.fabric}: %92 VISCOSE %8 ELIT</p>
              <p className="mt-2">💰 <s>28€</s> ➡️ <b>22€</b> 🔥</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
